// AuthLayout — shared visual frame for /login, /register, /forgot-password,
// /reset-password, /verify-email. Split-panel design that mirrors the
// SENTRY portal: dark gradient hero on the left with the brand mark,
// solid surface card on the right that hosts the form children.

import React from "react";
import { Link } from "react-router-dom";

interface Props {
  children: React.ReactNode;
  /** Title shown above the form (eg. "登入"，"註冊"). */
  title: string;
  /** Subtitle shown immediately below the title. */
  subtitle?: string;
}

export default function AuthLayout({ children, title, subtitle }: Props): React.ReactElement {
  return (
    <div className="min-h-screen w-full bg-bg-base text-text-primary flex flex-col">
      {/* Top bar — same white wordmark as the landing top nav. Click goes
          back to /. Mounted above the split panel so it sits over both
          the hero side (md+) and the form side (mobile). */}
      <TopBar />

      <div className="flex-1 flex flex-col md:flex-row">
        <HeroPanel />

        <section className="relative flex-1 flex flex-col items-center justify-center px-4 py-10 md:px-12">
        <div className="w-full max-w-[420px] card bg-bg-surface border-border p-6 md:p-8 animate-fade-in">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight text-text-primary">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>
            ) : null}
          </div>
          {children}
        </div>

        <p className="mt-6 text-xs text-text-muted">
          <Link to="/" className="hover:text-text-secondary transition-colors">
            ← 回首頁
          </Link>
        </p>
        </section>
      </div>
    </div>
  );
}

// ── Top bar ──────────────────────────────────────────────────────────────
// Same visual frame as the landing top bar (border-bottom + backdrop blur)
// so the auth surfaces feel like part of the same product. Logo is the
// white wordmark; clicking returns to /. The top bar collapses to logo
// only — there is no 主控台 CTA here because we ARE the auth surface.

function TopBar(): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-30 w-full border-b border-border backdrop-blur"
      style={{ background: "rgba(10, 25, 47, 0.78)" }}
    >
      <div className="mx-auto flex h-14 max-w-6xl items-center px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 no-underline" aria-label="回首頁">
          <img src="/sentry-logo.png" alt="SENTRY" className="h-6 w-auto opacity-90" />
          <span className="text-sm font-semibold tracking-wide text-text-primary">
            NEXUS
          </span>
        </Link>
      </div>
    </header>
  );
}

function HeroPanel(): React.ReactElement {
  return (
    <aside
      className="relative hidden md:flex md:flex-1 items-center justify-center overflow-hidden"
      style={{
        background:
          "linear-gradient(145deg, #081420 0%, #0d2030 60%, #061018 100%)",
      }}
    >
      {/* Subtle blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,212,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.05) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse at center, black 50%, transparent 80%)",
        }}
      />
      {/* Glow */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          width: 540,
          height: 540,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,212,255,0.18) 0%, transparent 70%)",
          filter: "blur(8px)",
        }}
      />
      <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-md">
        <img
          src="/sentry-shield.png"
          alt="SENTRY"
          width={200}
          height={200}
          className="opacity-90"
          style={{
            filter: "drop-shadow(0 0 48px rgba(0,212,255,0.35))",
          }}
        />
        <div className="mt-6 flex items-center gap-2">
          <img
            src="/sentry-logo.png"
            alt="SENTRY"
            className="h-7 w-auto opacity-90"
          />
          <span className="text-text-primary text-lg font-semibold tracking-wide">
            NEXUS
          </span>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary max-w-xs">
          AI EOS 的統一智慧入口——聚合你的知識、工作、行程與聯絡人，
          AI 理解完整工作情境並擬出計畫，經你確認後才執行。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Pill>統一 AI 入口</Pill>
          <Pill>AI 提案、你核准</Pill>
          <Pill>確定性執行與稽核</Pill>
        </div>
      </div>
    </aside>
  );
}

function Pill({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-xs"
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.08)",
        color: "rgba(255,255,255,0.6)",
      }}
    >
      {children}
    </span>
  );
}
